import React, {useState, useEffect} from "react";
import { Mail, Phone, Home, Shield, Clock } from "lucide-react";
import {WebLink, SiteWide, Images} from "./customComponents";
import {useNavigate} from 'react-router-dom';

import {authActions} from '../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import Helper1, {notify} from '../utility/Helper1';
import { userSelect, allRemoteSettingsSelect } from '../redux/selectors/index';
import Countdown from 'react-countdown';


function UserVerification(){

    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);

    const [emailCodeInput, setEmailCodeInput] = useState('') 
    const [phoneCodeInput, setPhoneCodeInput] = useState('') 
    const [verifyingEmailOrPhone, setVerifyingEmailOrPhone] = useState(0)
    const [isHovered, setIsHovered] = useState(false);
    const [focusedField, setFocusedField] = useState(null);

    const [fullPageLoading, setFullPageLoading]         = useState(false);
    const navigate                                       = useNavigate();

    const dispatch = useDispatch();

    const handleInputFocus = (field) => {
        setFocusedField(field);
    };

    const handleInputBlur = () => {
        setFocusedField(null);
    };

    const verifyEmail = (e) => {
        e.preventDefault();

        setFullPageLoading(true);
        
        dispatch(authActions.onSignUp("account/verify_email", {emailCode:emailCodeInput}, response => {

            if(response.code == 1){
                if((userData?.emailVerification == 1) && (userData?.phoneVerification == 1)){
                    navigate('/');
                }else{
                    setVerifyingEmailOrPhone(1)
                }
            }

            // notify(response.msg, response.code)
            setFullPageLoading(false);
        
        }),
        );

    }


    const resendEmailVerification = (e) => {
        e.preventDefault();

        setFullPageLoading(true);
        
        dispatch(authActions.onRefreshSystemData("account/resend_email_verification", 100, {}, response => {
            notify(response.msg, response.code)
            setFullPageLoading(false);
        }),
        );
    }


    const resendPhoneVerification = (e) => {
        e.preventDefault();

        setFullPageLoading(true);
        
        dispatch(authActions.onRefreshSystemData("account/resend_phone_verification", 100, {}, response => {
            notify(response.msg, response.code)
            setFullPageLoading(false);
        }),
        );
    }



    const verifyPhone = (e) => {
        e.preventDefault();

        setFullPageLoading(true);
        
        dispatch(authActions.onSignUp("account/verify_phone", {smsCode:phoneCodeInput}, response => {

            if(response.code == 1){
                navigate('/');
            }

            // notify(response.msg, response.code)
            setFullPageLoading(false);
        
        }),
        );

    }



    const doLogOutNowNow = () => {
        dispatch(authActions.onLogout())
    }

    useEffect(() => {
        if(userData?.emailVerification == 0){
            setVerifyingEmailOrPhone(0)
        }else if(userData?.phoneVerification == 0){
            setVerifyingEmailOrPhone(1)
        }
    }, [])

    return (
        <SiteWide hideMainDiv={true} hideMenu={true} title="Account verification">
            <div style={{backgroundColor: `#233950`}} className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-dark">
                {/* Animated background elements */}
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
                    <div className="absolute top-40 right-20 w-72 h-72 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
                    <div className="absolute -bottom-8 left-40 w-72 h-72 bg-gradient-to-r from-green-500 to-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-500"></div>
                </div>

                {/* Floating particles */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(20)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute w-2 h-2 bg-white rounded-full opacity-20 animate-bounce"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 2}s`,
                                animationDuration: `${2 + Math.random() * 2}s`
                            }}
                        ></div>
                    ))}
                </div>

                {/* Main verification container */}
                <div className="relative z-10 w-full max-w-md">
                    {/* Glowing border effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-2xl blur-sm opacity-75 animate-pulse"></div>

                    <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-gray-700/50">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <div className="relative inline-block">
                                <Images
                                    height="60px"
                                    src={site_settings?.site_settings?.logo || "/api/placeholder/200/60"}
                                    alt="logo"
                                    className="mx-auto mb-4 filter brightness-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-lg blur-lg opacity-30"></div>
                            </div>
                            <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-2">
                                {verifyingEmailOrPhone == 0 ? 'Verify Email' : 'Verify Phone'}
                            </h2>
                            <p className="text-gray-400 text-sm">
                                {verifyingEmailOrPhone == 0 ? 'Enter the code sent to your email' : 'Enter the code sent to your phone'}
                            </p>
                        </div>

                        <div className="space-y-6">
                            {/* Email Verification Form */}
                            {verifyingEmailOrPhone == 0 && (
                                <>
                                    <div className="relative group">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Email Verification Code
                                        </label>
                                        <div className="relative">
                                            <div className={`absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg transition-opacity duration-300 ${focusedField === 'emailCode' ? 'opacity-100' : 'opacity-0'}`}></div>
                                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                                            <input
                                                type="text"
                                                value={emailCodeInput}
                                                onChange={(e) => setEmailCodeInput(e.target.value)}
                                                onFocus={() => handleInputFocus('emailCode')}
                                                onBlur={handleInputBlur}
                                                placeholder="Enter verification code"
                                                className="relative w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400 transition-all duration-300 backdrop-blur-sm"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Verify Email Button */}
                                    <button
                                        type="button"
                                        disabled={fullPageLoading}
                                        onClick={verifyEmail}
                                        onMouseEnter={() => setIsHovered(true)}
                                        onMouseLeave={() => setIsHovered(false)}
                                        className="relative w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden group"
                                    >
                                        {/* Button background gradient */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 transition-opacity duration-300"></div>
                                        <div className={`absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}></div>

                                        {/* Button glow effect */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>

                                        <div className="relative flex items-center justify-center space-x-2">
                                            {fullPageLoading ? (
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            ) : (
                                                <>
                                                    <Shield className="w-5 h-5" />
                                                    <span>Verify Email</span>
                                                </>
                                            )}
                                        </div>
                                    </button>

                                    {/* Resend Email Code */}
                                    <div className="flex justify-center">
                                        <Countdown
                                            zeroPadTime={2}
                                            key={new Date(userData?.lastEmailTime)}
                                            date={new Date(userData?.lastEmailTime)}
                                            renderer={({ hours, minutes, seconds, completed }) => {
                                                if (completed) {
                                                    return (
                                                        <button 
                                                            onClick={resendEmailVerification}
                                                            className="text-cyan-400 hover:text-cyan-300 transition-colors duration-200 hover:underline text-sm"
                                                        >
                                                            Resend Email Code
                                                        </button>
                                                    );
                                                } else {
                                                    return (
                                                        <span className="text-gray-400 text-sm flex items-center space-x-1">
                                                            <Clock className="w-4 h-4" />
                                                            <span>Resend in {minutes}:{seconds}</span>
                                                        </span>
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Phone Verification Form */}
                            {verifyingEmailOrPhone == 1 && (
                                <>
                                    <div className="relative group">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Phone Verification Code
                                        </label>
                                        <div className="relative">
                                            <div className={`absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg transition-opacity duration-300 ${focusedField === 'phoneCode' ? 'opacity-100' : 'opacity-0'}`}></div>
                                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                                            <input
                                                type="text"
                                                value={phoneCodeInput}
                                                onChange={(e) => setPhoneCodeInput(e.target.value)}
                                                onFocus={() => handleInputFocus('phoneCode')}
                                                onBlur={handleInputBlur}
                                                placeholder="Enter verification code"
                                                className="relative w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400 transition-all duration-300 backdrop-blur-sm"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Verify Phone Button */}
                                    <button
                                        type="button"
                                        disabled={fullPageLoading}
                                        onClick={verifyPhone}
                                        onMouseEnter={() => setIsHovered(true)}
                                        onMouseLeave={() => setIsHovered(false)}
                                        className="relative w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden group"
                                    >
                                        {/* Button background gradient */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 transition-opacity duration-300"></div>
                                        <div className={`absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}></div>

                                        {/* Button glow effect */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>

                                        <div className="relative flex items-center justify-center space-x-2">
                                            {fullPageLoading ? (
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            ) : (
                                                <>
                                                    <Shield className="w-5 h-5" />
                                                    <span>Verify Phone</span>
                                                </>
                                            )}
                                        </div>
                                    </button>

                                    {/* Resend Phone Code */}
                                    <div className="flex justify-center">
                                        <Countdown
                                            zeroPadTime={2}
                                            key={new Date(userData?.lastSMSTime)}
                                            date={new Date(userData?.lastSMSTime)}
                                            renderer={({ hours, minutes, seconds, completed }) => {
                                                if (completed) {
                                                    return (
                                                        <button 
                                                            onClick={resendPhoneVerification}
                                                            className="text-purple-400 hover:text-purple-300 transition-colors duration-200 hover:underline text-sm"
                                                        >
                                                            Resend Phone Code
                                                        </button>
                                                    );
                                                } else {
                                                    return (
                                                        <span className="text-gray-400 text-sm flex items-center space-x-1">
                                                            <Clock className="w-4 h-4" />
                                                            <span>Resend in {minutes}:{seconds}</span>
                                                        </span>
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Logout Link */}
                            <div className="flex justify-center pt-4">
                                <button
                                    onClick={doLogOutNowNow}
                                    className="text-gray-400 hover:text-gray-300 transition-colors duration-200 hover:underline text-sm"
                                >
                                    Log out
                                </button>
                            </div>
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-t-2xl"></div>
                    </div>
                </div>

                {/* Bottom navigation icons */}
                <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-20">
                    <div className="flex space-x-6 bg-gray-900/70 backdrop-blur-lg px-6 py-3 rounded-full border border-gray-700/50">
                        {[
                            { icon: Home, to: '/', color: 'text-cyan-400' },
                        ].map(({ icon: Icon, color, to }, index) => (
                            <button
                                onClick={() => navigate(to)}
                                key={index}
                                className={`${color} hover:scale-110 transition-transform duration-200 p-2 rounded-full hover:bg-white/10`}
                            >
                                <Icon className="w-5 h-5" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </SiteWide>
    );

}

export default UserVerification;